import {InfiniteData} from '@tanstack/query-core';
import {
  infiniteQueryOptions,
  queryOptions,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import {SEARCH_QUERY_KEY_MAP} from '../api/search/constant';
import {getSearchAutocomplete} from '../api/search/getSearchAutocomplete';
import {getSearchFilter} from '../api/search/getSearchFilter';
import {getSearchMain} from '../api/search/getSearchMain';
import {getSearchSeries} from '../api/search/getSearchSeries';
import {FetcherOptions} from '../axios/axiosBffFetch';
import {requestContext} from '../common/requestContext';
import {SearchKeywordSortTypeVo} from '../common/shared/api/nicolas/vo/SearchKeywordSortTypeVo.enum';
import {SearchThemeKeywordSortTypeVo} from '../common/shared/api/nicolas/vo/SearchThemeKeywordSortTypeVo.enum';
import {PagewebError} from '../errors/PagewebError.class';
import {createSearchAutoComplete} from '../graphql/factory/SearchAutoComplete';
import {createSearchKeyword} from '../graphql/factory/SearchKeyword';
import {createSearchMain} from '../graphql/factory/SearchMain';
import {createSearchSortTypeVo} from '../graphql/factory/SearchSortType';
import {createSearchThemeKeyword} from '../graphql/factory/SearchThemeKeyword';
import {
  SearchAutoCompleteQuery,
  SearchAutoCompleteQueryVariables,
  SearchKeywordQuery,
  SearchKeywordQueryVariables,
  SearchMainQuery,
  SearchMainQueryVariables,
  SearchThemeKeywordQuery,
  SearchThemeKeywordQueryVariables,
} from '../graphql/generated/types';

/**
 * Search 도메인 Query Factory
 */
export const searchFactory = {
  /**
   * 검색 자동완성
   *
   * Origin: useSearchAutoCompleteQuery
   * Resolver 로직: 단순 API 호출 + Factory 변환
   */
  searchAutoComplete: (
    variables: SearchAutoCompleteQueryVariables,
    options?: Omit<
      UseQueryOptions<
        SearchAutoCompleteQuery,
        PagewebError,
        SearchAutoCompleteQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: SEARCH_QUERY_KEY_MAP.searchAutoComplete(variables),
      queryFn: async () => {
        const {keyword} = variables.input;
        // V2 자동완성은 page 파라미터를 지원하나, 현 화면은 1페이지(기본 size=10)
        // 후보만 사용하므로 page=0 고정. 무한스크롤 도입 시 확장.
        const response = await getSearchAutocomplete(
          {keyword, page: 0},
          fetcherOptions
        );
        const data = response.result;

        return {
          searchAutoComplete: data ? createSearchAutoComplete(data) : undefined,
        };
      },
      ...options,
    }),

  /**
   * 검색 메인 (인기 검색어)
   *
   * Origin: useSearchMainQuery
   * Resolver 로직: 단순 API 호출 + Factory 변환 (파라미터 없음)
   */
  searchMain: (
    variables?: SearchMainQueryVariables,
    options?: Omit<
      UseQueryOptions<SearchMainQuery, PagewebError, SearchMainQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: SEARCH_QUERY_KEY_MAP.searchMain(variables),
      queryFn: async () => {
        const response = await getSearchMain(fetcherOptions);
        const data = response.result;

        return {
          searchMain: data ? createSearchMain(data) : undefined,
        };
      },
      ...options,
    }),

  /**
   * 검색 키워드 결과 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   *
   * 사용법:
   * ```tsx
   * const {data, fetchNextPage, hasNextPage} = useInfiniteQuery(
   *   searchQueryFactory.infiniteSearchKeyword(
   *     {
   *       input: {
   *         keyword: '검색어',
   *         page: 0,
   *         size: 25,
   *       }
   *     },
   *     {
   *       initialPageParam: 0,
   *       getNextPageParam: (lastPage, allPages) => {
   *         return !lastPage.isEnd ? allPages.length : undefined;
   *       }
   *     }
   *   )
   * );
   * // data.pages는 SearchKeyword[] 타입
   * // 모든 아이템: data.pages.flatMap(page => page.list)
   * ```
   */
  infiniteSearchKeyword: (
    variables: SearchKeywordQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SearchKeywordQuery,
        PagewebError,
        InfiniteData<SearchKeywordQuery, SearchKeywordQueryVariables>,
        SearchKeywordQuery,
        ReturnType<typeof SEARCH_QUERY_KEY_MAP.infiniteSearchKeyword>,
        SearchKeywordQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: SEARCH_QUERY_KEY_MAP.infiniteSearchKeyword(variables),
      queryFn: async ({pageParam}) => {
        const {sortType, categoryUid} = pageParam.input;

        // Resolver 로직: categoryUid를 Number로 변환 (있고 length가 있는 경우)
        // sortType을 SearchKeywordSortTypeVo로 변환
        const params = {
          ...variables.input,
          page: pageParam.input.page,
          categoryUid: categoryUid?.length ? Number(categoryUid) : undefined,
          sortType:
            sortType &&
            (createSearchSortTypeVo(sortType) as SearchKeywordSortTypeVo),
        };

        const response = await getSearchSeries(params, fetcherOptions);
        const userInfo = requestContext.requestInfo?.user ?? null;

        // ⚠️ Resolver와 동일하게 factory 적용
        return {
          searchKeyword: createSearchKeyword(
            userInfo,
            response.result,
            pageParam.input.page || 0
          ),
        };
      },
      ...options, // initialPageParam, getNextPageParam 포함
    }),

  /**
   * 테마 검색 키워드 결과 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   * ⚠️ queryFn에서 factory 적용 → Resolver와 동일하게 각 페이지마다 변환
   *
   * 사용법:
   * ```tsx
   * const {data, fetchNextPage, hasNextPage} = useInfiniteQuery(
   *   searchQueryFactory.infiniteSearchThemeKeyword(
   *     {
   *       input: {
   *         filterList: ['filter1', 'filter2'],
   *         page: 0,
   *         size: 25,
   *       }
   *     },
   *     {
   *       initialPageParam: 0,
   *       getNextPageParam: (lastPage, allPages) => {
   *         return !lastPage.isEnd ? allPages.length : undefined;
   *       }
   *     }
   *   )
   * );
   * // data.pages는 SearchThemeKeyword[] 타입
   * // 모든 아이템: data.pages.flatMap(page => page.list)
   * ```
   */
  infiniteSearchThemeKeyword: (
    variables: SearchThemeKeywordQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        SearchThemeKeywordQuery,
        PagewebError,
        InfiniteData<SearchThemeKeywordQuery, SearchThemeKeywordQueryVariables>,
        SearchThemeKeywordQuery,
        ReturnType<typeof SEARCH_QUERY_KEY_MAP.infiniteSearchThemeKeyword>,
        SearchThemeKeywordQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: SEARCH_QUERY_KEY_MAP.infiniteSearchThemeKeyword(variables),
      queryFn: async ({pageParam}) => {
        const {sortType} = variables.input;

        // Resolver 로직: sortType을 SearchThemeKeywordSortTypeVo로 변환
        const convertedSortType =
          sortType &&
          (createSearchSortTypeVo(sortType) as SearchThemeKeywordSortTypeVo);

        // API Fetcher에서 기본값 처리를 하므로 단순히 값만 전달
        const params = {
          filterList: variables.input.filterList,
          page: pageParam.input.page,
          size: variables.input.size,
          categoryUid: variables.input.categoryUid,
          ...(convertedSortType && {sortType: convertedSortType}),
        };

        const response = await getSearchFilter(params, fetcherOptions);
        const userInfo = requestContext.requestInfo?.user ?? null;

        // ⚠️ Resolver와 동일하게 factory 적용
        return {
          searchThemeKeyword: createSearchThemeKeyword(
            userInfo,
            response.result,
            pageParam.input.page || 0
          ),
        };
      },
      ...options, // initialPageParam, getNextPageParam 포함
    }),
} as const;
